import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  DonationForm,
  IPersonsListParams,
  IPredicate,
  TPredicateMode,
} from '@feathr/blackbox';
import { EFlavorsRaw } from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import { CardV2, TanStackTable, Value } from '@feathr/components';

// Matches TanStackTable's ISorted (desc?: boolean), which the package index does
// not re-export (the exported ISorted is the legacy Table's desc?: true shape).
type TSort = { id: string; desc?: boolean };
import { useStore } from '@feathr/report_components/state';

import DonationFormCreateGroupButton from './DonationFormCreateGroupButton';
import { useDonationFormPersonsList } from './DonationFormEngagementCard.hooks';
import donationFormEngagementColumns from './donationFormEngagementColumns';
import DonationFormInteractionSelect, {
  donationEngagementFlavorLabel,
  EDonationEngagementFlavor,
} from './DonationFormInteractionSelect';

import * as styles from './DonationFormEngagementCard.css';

interface IEngagementPredicateGroup {
  kind?: 'activity';
  group: IPredicate[];
  group_mode?: TPredicateMode;
  excludes?: boolean;
  inner_hits?: {
    docvalue_fields: string[];
    name: string;
    size: number;
    sort: { 'breadcrumbs.d_c': string };
  };
}

export interface IDonationEngagementParams extends Omit<IPersonsListParams, 'predicates'> {
  predicates: IEngagementPredicateGroup[];
}

interface IProps {
  form: DonationForm;
  dateRange: {
    start: string;
    end: string;
  };
  localUrl: ((url: string) => string) | undefined;
}

/*
 * Builds the `/v1/persons/page/` predicates for a donation form engagement
 * query. When dateStart/dateEnd are provided, adds `d_c` bounds so the list
 * is scoped to the same timeframe as the page's KPI tiles and Trends chart.
 */
export function getDonationFormPredicates(
  formId: string,
  flavor: EDonationEngagementFlavor,
  dateStart?: string,
  dateEnd?: string,
): IEngagementPredicateGroup[] {
  const isViewedFlavor =
    flavor === EDonationEngagementFlavor.Viewed ||
    flavor === EDonationEngagementFlavor.ViewedNoDonation;

  const group: IPredicate[] = [
    {
      kind: 'activity',
      attr_against: 'form_id',
      attr_type: 'string',
      comparison: 'eq',
      value: formId,
    },
  ];

  if (isViewedFlavor) {
    group.push({
      kind: 'activity',
      attr_against: 'form_type',
      attr_type: 'string',
      comparison: 'eq',
      value: 'donation',
    });
    group.push({
      kind: 'activity',
      attr_against: 'flvr',
      attr_type: 'string',
      comparison: 'eq',
      value: EFlavorsRaw.form_view,
    });
  } else {
    group.push({
      kind: 'activity',
      attr_against: 'flvr',
      attr_type: 'string',
      comparison: 'eq',
      value: EFlavorsRaw.donation,
    });
    group.push({
      kind: 'activity',
      attr_against: 'donation.frequency',
      attr_type: 'string',
      comparison: 'eq',
      value: flavor === EDonationEngagementFlavor.OneTime ? 'one_time' : 'monthly',
    });
  }

  if (dateStart) {
    group.push({
      kind: 'activity',
      attr_against: 'd_c',
      attr_type: 'date',
      comparison: 'gte_date',
      value: dateStart,
    });
  }
  if (dateEnd) {
    group.push({
      kind: 'activity',
      attr_against: 'd_c',
      attr_type: 'date',
      comparison: 'lte_date',
      value: dateEnd,
    });
  }

  const groups: IEngagementPredicateGroup[] = [
    {
      group,
      inner_hits: {
        name: 'first_flavor',
        sort: { 'breadcrumbs.d_c': 'asc' },
        size: 1,
        docvalue_fields: [
          'breadcrumbs.d_c',
          'breadcrumbs.donation.amount',
          'breadcrumbs.donation.currency',
          'breadcrumbs.donation.frequency',
          'breadcrumbs.growth_credits',
        ],
      },
    },
  ];

  if (flavor === EDonationEngagementFlavor.ViewedNoDonation) {
    // Exclude people who have ever donated on this form. Unbounded on
    // purpose: a past donor isn't a prospect regardless of view timing.
    groups.push({
      kind: 'activity',
      group: [
        {
          kind: 'activity',
          attr_against: 'form_id',
          attr_type: 'string',
          comparison: 'eq',
          value: formId,
        },
        {
          kind: 'activity',
          attr_against: 'flvr',
          attr_type: 'string',
          comparison: 'eq',
          value: EFlavorsRaw.donation,
        },
      ],
      group_mode: 'match_all',
      excludes: true,
    });
  }

  return groups;
}

function DonationFormEngagementCard({ form, dateRange, localUrl }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const { Persons } = useStore();

  const [flavor, setFlavor] = useState<EDonationEngagementFlavor>(EDonationEngagementFlavor.Viewed);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState<TSort[]>([]);

  const ordering = sort.length
    ? sort.map(({ id, desc }) => `${desc ? '-' : ''}${id}`)
    : ['-date_last_seen'];

  const params: IDonationEngagementParams = {
    mode: flavor === EDonationEngagementFlavor.ViewedNoDonation ? 'match_all' : 'match_any',
    lookback_mode: 'unbounded',
    exclude: ['breadcrumbs'],
    ordering,
    pagination: { page, page_size: pageSize },
    predicates: getDonationFormPredicates(form.id, flavor, dateRange.start, dateRange.end),
  };

  function handleSelectFlavor({ id }: ISelectOption): void {
    setFlavor(id as EDonationEngagementFlavor);
    // A previously-selected flavor's page position isn't meaningful for a
    // newly-selected one, and leaving it in place risks landing past the end
    // of the new result set.
    setPage(0);
  }

  function handlePageChange(newPage: number): void {
    setPage(newPage);
  }

  function handlePageSizeChange(newPageSize: number, newPage: number): void {
    setPageSize(newPageSize);
    setPage(newPage);
  }

  function handleSortChange(newSorted: TSort[]): void {
    setSort(newSorted);
    // A new ordering invalidates the previous page's row positions.
    setPage(0);
  }

  const key = `${form.id}|${flavor}|${dateRange.start}|${dateRange.end}`;
  const personsList = useDonationFormPersonsList(Persons, params, Persons.url('page'), key);
  const uniqueCount = personsList.isPending ? 0 : personsList.pagination.count;

  const isAnonymousReport = !localUrl;

  return (
    <CardV2 width={'full'}>
      <CardV2.Header title={t('Donation form engagement')}>
        {!isAnonymousReport && (
          <DonationFormCreateGroupButton
            flavor={flavor}
            form={form}
            localUrl={localUrl}
            params={params}
          />
        )}
      </CardV2.Header>
      <CardV2.Content padding={'relaxed'}>
        <div className={styles.header}>
          <div className={styles.selectWrapper}>
            <DonationFormInteractionSelect flavor={flavor} onSelectSingle={handleSelectFlavor} />
            <Value
              className={styles.totalInteractions}
              label={t('Unique {{flavor}}:', {
                flavor: donationEngagementFlavorLabel(t)[flavor].toLowerCase(),
              })}
              value={t('{{count}} Person', { count: uniqueCount })}
            />
          </div>
        </div>
        <TanStackTable
          columns={donationFormEngagementColumns(t, localUrl, flavor)}
          idKey={'id'}
          isLoading={personsList.isPending}
          items={personsList.models}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSortChange={handleSortChange}
          page={page}
          pages={personsList.pagination.pages}
          pageSize={pageSize}
          sort={sort}
        />
      </CardV2.Content>
    </CardV2>
  );
}

export { donationEngagementFlavorLabel, EDonationEngagementFlavor };
export default observer(DonationFormEngagementCard);
